package com.solevo.dist.components

import androidx.compose.runtime.*
import com.solevo.dist.application.models.Notification
import com.solevo.dist.util.DateFormatter
import com.solevo.dist.util.Res
import com.solevo.dist.viewmodels.NotificationsTab
import com.solevo.dist.viewmodels.NotificationsUiState
import com.solevo.dist.viewmodels.NotificationsViewModel
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color.Companion.rgb
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.datetime.Clock
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.css.*

@Composable
fun NotificationsContent(
    ui: NotificationsUiState,
    viewModel: NotificationsViewModel,
    onNotificationClick: ((String) -> Unit)? = null
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
    ) {
        // Tabs
        NotificationsTabs(
            selectedTab = ui.selectedTab,
            onTabSelected = { viewModel.selectTab(it) }
        )

        Box(Modifier.height(24.px))

        // Notifications List
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(leftRight = 24.px)
        ) {
            if (ui.loading && ui.notifications.isEmpty()) {
                CenteredLoader()
            } else if (ui.notifications.isEmpty()) {
                EmptyState(
                    title = "No notifications",
                    message = when (ui.selectedTab) {
                        NotificationsTab.Promotion -> "No promotions available"
                        NotificationsTab.Orders -> "No order notifications"
                        NotificationsTab.Quotes -> "No quote notifications"
                        NotificationsTab.LoyaltyTransaction -> "No loyalty transaction notifications"
                        NotificationsTab.All -> "No notifications yet"
                    }
                )
            } else {
                NotificationsList(
                    notifications = ui.notifications,
                    revealedNotification = ui.revealedNotification,
                    onNotificationClick = onNotificationClick,
                    onDelete = { date, id -> viewModel.removeNotification(date, id) },
                    onToggleRevealed = { viewModel.updateRevealedItem(it) }
                )
            }

            // Loading indicator for load more
            if (ui.isLoading && ui.notifications.isNotEmpty()) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 16.px),
                    contentAlignment = Alignment.Center
                ) {
                    CenteredLoader()
                }
            }
        }
    }
}

@Composable
private fun NotificationsTabs(
    selectedTab: NotificationsTab,
    onTabSelected: (NotificationsTab) -> Unit
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .padding(leftRight = 24.px)
            .styleModifier {
                property("overflow-x", "auto")
                property("overflow-y", "hidden")
                property("-webkit-overflow-scrolling", "touch")
                property("scrollbar-width", "none") // Firefox
                property("-ms-overflow-style", "none") // IE/Edge
            }
    ) {
        Row(
            modifier = Modifier
                .gap(16.px)
                .styleModifier {
                    property("display", "flex")
                    property("flex-wrap", "nowrap")
                    property("width", "max-content")
                }
        ) {
            NotificationsTab.values().forEach { tab ->
                NotificationTab(
                    label = tab.label,
                    selected = selectedTab == tab,
                    onClick = { onTabSelected(tab) }
                )
            }
        }
    }
}

@Composable
private fun NotificationTab(
    label: String,
    selected: Boolean,
    onClick: () -> Unit
) {
    Box(
        modifier = Modifier
            .cursor(Cursor.Pointer)
            .padding(leftRight = 12.px, topBottom = 10.px)
            .styleModifier {
                if (selected) {
                    property("box-shadow", "inset 0 -3px 0 0 black")
                }
            }
            .onClick {
                onClick()
                // Prevent event propagation to avoid closing the panel
            }
    ) {
        SpanText(
            label,
            modifier = Modifier
                .fontSize(15.px)
                .fontWeight(if (selected) FontWeight.Bold else FontWeight.Normal)
                .color(rgb(36, 41, 47))
        )
    }
}

@Composable
private fun NotificationsList(
    notifications: Map<LocalDateTime, List<Notification>>,
    revealedNotification: String?,
    onNotificationClick: ((String) -> Unit)?,
    onDelete: (LocalDateTime, String) -> Unit,
    onToggleRevealed: (String?) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .gap(0.px)
    ) {
        notifications.forEach { (date, notificationList) ->
            // Date header
            DateHeader(date)
            Box(Modifier.height(10.px))

            // Notifications for this date
            notificationList.forEachIndexed { index, notification ->
                NotificationItem(
                    notification = notification,
                    isRevealed = notification.notificationId == revealedNotification,
                    showDivider = index < notificationList.lastIndex,
                    onNotificationClick = onNotificationClick,
                    onDelete = { onDelete(date, notification.notificationId ?: "") },
                    onToggleRevealed = { toggled ->
                        onToggleRevealed(if (toggled) notification.notificationId else null)
                    }
                )
            }

            Box(Modifier.height(16.px))
        }
    }
}

@Composable
private fun DateHeader(date: LocalDateTime) {
    val dateText = formatNotificationDate(date)
    SpanText(
        dateText,
        modifier = Modifier
            .fontSize(13.px)
            .fontWeight(FontWeight.SemiBold)
            .color(rgb(120, 130, 140))
            .padding(topBottom = 8.px)
    )
}

@Composable
private fun NotificationItem(
    notification: Notification,
    isRevealed: Boolean,
    showDivider: Boolean,
    onNotificationClick: ((String) -> Unit)?,
    onDelete: (String) -> Unit,
    onToggleRevealed: (Boolean) -> Unit
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .backgroundColor(if (notification.status == com.solevo.dist.application.models.NotificationStatus.New) {
                rgb(245, 247, 250)
            } else {
                rgb(255, 255, 255)
            })
            .padding(16.px)
            .then(
                if (onNotificationClick != null && notification.notificationId != null) {
                    Modifier
                        .cursor(Cursor.Pointer)
                        .onClick { onNotificationClick(notification.notificationId!!) }
                } else {
                    Modifier
                }
            )
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.Top
        ) {
            Column(
                modifier = Modifier
                    .flexGrow(1f)
                    .gap(4.px)
            ) {
                notification.title?.let { title ->
                    SpanText(
                        title,
                        modifier = Modifier
                            .fontSize(15.px)
                            .fontWeight(FontWeight.SemiBold)
                            .color(rgb(36, 41, 47))
                    )
                }

                notification.message?.let { message ->
                    SpanText(
                        message,
                        modifier = Modifier
                            .fontSize(14.px)
                            .color(rgb(120, 130, 140))
                    )
                }

                notification.timestamp?.let { timestamp ->
                    val timeText = formatNotificationTime(timestamp)
                    SpanText(
                        timeText,
                        modifier = Modifier
                            .fontSize(12.px)
                            .color(rgb(160, 170, 180))
                            .padding(top = 4.px)
                    )
                }
            }

            // Delete button
            if (isRevealed) {
                Image(
                    src = Res.Icon.delete,
                    alt = "Delete",
                    modifier = Modifier
                        .size(18.px)
                        .cursor(Cursor.Pointer)
                        .onClick {
                            notification.notificationId?.let { onDelete(it) }
                        }
                )
            }
        }

        // "New" badge
        if (notification.status == com.solevo.dist.application.models.NotificationStatus.New) {
            Box(Modifier.height(8.px))
            Row(
                modifier = Modifier
                    .align(Alignment.Start)
            ) {
                Box(
                    modifier = Modifier
                        .padding(leftRight = 8.px, topBottom = 4.px)
                        .backgroundColor(rgb(30, 100, 255))
                        .borderRadius(12.px)
                ) {
                    SpanText(
                        "New",
                        modifier = Modifier
                            .fontSize(11.px)
                            .fontWeight(FontWeight.SemiBold)
                            .color(rgb(255, 255, 255))
                            .padding(leftRight = 8.px, topBottom = 2.px)
                    )
                }
            }
        }

        if (showDivider) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.px)
                    .backgroundColor(rgb(240, 242, 246))
                    .padding(top = 16.px)
            )
        }
    }
}

private fun formatNotificationDate(date: LocalDateTime): String {
    val timeZone = TimeZone.currentSystemDefault()
    val now = Clock.System.now().toLocalDateTime(timeZone)
    val today = now.date
    // Calculate yesterday by subtracting one day using DateTimeUnit
    val yesterday = today.minus(1, DateTimeUnit.DAY)
    val dateOnly = date.date

    return when {
        dateOnly == today -> "Today"
        dateOnly == yesterday -> "Yesterday"
        else -> {
            val month = date.month.name.lowercase().replaceFirstChar { it.titlecase() }.take(3)
            "${date.dayOfMonth} $month ${date.year}"
        }
    }
}

private fun formatNotificationTime(timestamp: kotlinx.datetime.Instant): String {
    val ldt = timestamp.toLocalDateTime(TimeZone.currentSystemDefault())
    val hour = ldt.hour.toString().padStart(2, '0')
    val minute = ldt.minute.toString().padStart(2, '0')
    return "$hour:$minute"
}

